import React, { useState } from "react";
import {
  FileText,
  AlertTriangle,
  Activity,
  Heart,
  Brain,
  Microscope,
  Wind,
  User,
  ChevronDown,
  ChevronUp,
  Save,
  Pencil,
  X,
  Hospital,
  ClipboardList,
} from "lucide-react";
import { usePacientes } from "../context/PacientesContext";
import { useExames } from "../../exames/context/ExamesContext";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import {
  calcularAbstinencia,
  MESES_ABSTINENCIA_EXIGIDOS,
} from "../../../shared/utils/abstinenciaAlcoolica";
import { calcularMELD } from "../../../utils/meld";

// 🩺 B248-FU (AI_118) — MELD calculado AO VIVO a partir dos componentes do exame
// (mesma lógica/gate da planilha de Exames Lab: ExamesLaboratoriais.tsx:2347),
// caindo no valor GRAVADO só como fallback. Sem isto, a Avaliação Clínica lia o
// `meld` persistido — vazio em exames importados/antigos/parciais — e mostrava
// "—" ainda que a planilha exibisse o MELD (calculado ao vivo). Bug recorrente.
function meldVivoDoExame(e: any, sexoFeminino: boolean): number {
  if (
    e?.creatinina &&
    e?.bilirrubinaTotal &&
    e?.inr &&
    e?.sodio &&
    e?.albumina
  ) {
    return calcularMELD({
      creatinina: Number(e.creatinina),
      bilirrubina: Number(e.bilirrubinaTotal),
      inr: Number(e.inr),
      sodio: Number(e.sodio),
      albumina: Number(e.albumina),
      sexoFeminino,
    }).score;
  }
  return Number(e?.meld) || 0; // fallback ao valor gravado
}

const CAUSAS_CIRROSE = [
  "Hepatite B",
  "Hepatite C",
  "Cirrose Alcoólica",
  "Hepatite Fulminante",
  "Cirrose criptogênica",
  "Esquistossomose hepatoesplênica",
  "Atresia de vias biliares",
  "Doença de Wilson",
  "Doença de Caroli",
  "Polineuropatia Amiloidótica Familiar – PAF",
  "Hemocromatose",
  "Síndrome de Budd Chiari",
  "Doenças metabólicas com repercussão grave no fígado",
  "Colangite Biliar Primária",
  "Colangite Esclerosante",
  "Cirrose Biliar Secundária",
  "Hepatite Autoimune",
  "Hepatite Alcoólica Aguda Grave",
  "Metástase hepática de tumor neuroendócrino",
  "Cirrose por doença esteatótica associada a disfunção metabólica",
  "Deficiência de alfa 1 antitripsina",
  "Adenomatose hepática múltipla irressecável",
  "Doença Hepática policística com síndrome compartimental",
  "Hemangiomatose hepática com Síndrome Compartimental",
  // AI_145 — colangiocarcinoma: indicação de TX emergente/especializada (2 subtipos
  // distintos no caminho de transplante). Fiado à IA via causaCirrose + bloco no
  // system prompt (promptsClinicos.js). SE ainda não prevista na legislação BR →
  // via exceção à Câmara Técnica (não entra em TIPOS_SE por ora).
  "Colangiocarcinoma peri-hilar (tumor de Klatskin)",
  "Colangiocarcinoma intra-hepático",
  "Outros",
];

const TIPOS_SE = [
  // I - Tumor neuroendócrino metastático
  "Tumor Neuroendócrino Metastático Irressecável",
  // II - CHC ≥2cm dentro de Milão
  "Carcinoma Hepatocelular (CHC)",
  // III - Hepatoblastoma
  "Hepatoblastoma",
  // IV - Síndrome hepatopulmonar
  "Síndrome Hepatopulmonar (PaO2 <60mmHg)",
  // V - Hemangioma gigante / policística compartimental
  "Hemangioma Gigante Irressecável / Hemangiomatose / Doença Policística com Síndrome Compartimental",
  // VI - Carcinoma fibrolamelar
  "Carcinoma Fibrolamelar Irressecável",
  // VII - Hemangioendotelioma epitelióide
  "Hemangioendotelioma Epitelióide Primário de Fígado Irressecável",
  // VIII - Adenomatose múltipla
  "Adenomatose Múltipla Bilobar Irressecável",
  // IX - Doenças metabólicas (<12 anos)
  "Doença Metabólica com Indicação de Transplante",
  // X - Polineuropatia amiloidótica familiar
  "Polineuropatia Amiloidótica Familiar (PAF)",
  // XI - Ascite refratária / hidrotórax
  "Ascite Refratária ou Hidrotórax Refratário",
  // XII - Colangites de repetição
  "Colangites de Repetição",
  // XIII - Encefalopatia hepática
  "Encefalopatia Hepática Crônica Refratária",
  // XIV - Prurido incapacitante
  "Prurido Incapacitante",
  // XV - CHC por downstaging
  "Carcinoma Hepatocelular por Downstaging",
  // XVI - Trombose aguda de artéria hepática (até 15º dia pós-Tx)
  "Trombose Aguda de Artéria Hepática Pós-Transplante",
  // XVII - Trombose tardia de artéria hepática (após 15º dia pós-Tx)
  "Trombose Tardia de Artéria Hepática Pós-Transplante",
];

interface Props {
  pacienteId: string;
  somenteLeitura?: boolean;
}

const fieldStyle = (editing: boolean): React.CSSProperties => ({
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${editing ? theme.primary[300] : theme.gray[300]}`,
  borderRadius: theme.borderRadius.md,
  fontSize: "14px",
  fontFamily: theme.fonts.sans,
  boxSizing: "border-box",
  background: editing ? "white" : theme.gray[50],
  color: theme.gray[800],
  cursor: editing ? "text" : "default",
});

const selectStyle = (editing: boolean): React.CSSProperties => ({
  ...fieldStyle(editing),
  cursor: editing ? "pointer" : "default",
  pointerEvents: editing ? "auto" : "none",
});

const labelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: "600",
  color: theme.gray[700],
  marginBottom: "6px",
  fontFamily: theme.fonts.sans,
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: "700",
  color: theme.gray[800],
  fontFamily: theme.fonts.sans,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  margin: "0 0 16px 0",
  paddingBottom: "8px",
  borderBottom: `2px solid ${theme.gray[200]}`,
};

const simNaoStyle = (
  valor: boolean | undefined,
  editing: boolean,
  opcao: boolean
): React.CSSProperties => ({
  flex: 1,
  padding: "8px",
  border: `1px solid ${
    valor === opcao ? (opcao ? theme.primary[500] : "#ef4444") : theme.gray[300]
  }`,
  borderRadius: theme.borderRadius.md,
  background:
    valor === opcao ? (opcao ? theme.primary[50] : "#fef2f2") : "white",
  color:
    valor === opcao
      ? opcao
        ? theme.primary[700]
        : "#dc2626"
      : theme.gray[600],
  fontWeight: valor === opcao ? "700" : "400",
  fontSize: "13px",
  cursor: editing ? "pointer" : "default",
  fontFamily: theme.fonts.sans,
  pointerEvents: editing ? "auto" : "none",
});

export default function AbaAvaliacaoClinica({
  pacienteId,
  somenteLeitura = false,
}: Props) {
  const { pacientes, atualizarPaciente } = usePacientes();
  const { exames } = useExames();
  const paciente = pacientes.find((p) => p.id === pacienteId);
  const [editing, setEditing] = useState(false);
  const [dados, setDados] = useState<any>(null);

  if (!paciente) return null;

  const av = editing ? dados : paciente.avaliacaoClinica || {};
  const insc = editing ? dados?.inscricaoTx : paciente.inscricaoTx || {};

  const set = (campo: string, valor: any) => {
    setDados((prev: any) => ({ ...prev, [campo]: valor }));
  };

  const setInsc = (campo: string, valor: any) => {
    setDados((prev: any) => ({
      ...prev,
      inscricaoTx: {
        ...(prev?.inscricaoTx || paciente.inscricaoTx || {}),
        [campo]: valor,
      },
    }));
  };

  const handleEditar = () => {
    // Converter AAAA-MM-DD → DD/MM/AAAA para exibição nos inputs com máscara
    const paraBR = (data: string): string => {
      if (!data) return data;
      if (/^\d{4}-\d{2}-\d{2}/.test(data)) {
        const p = data.split("T")[0].split("-");
        return `${p[2]}/${p[1]}/${p[0]}`;
      }
      return data;
    };

    const insc = { ...(paciente.inscricaoTx || {}) };
    if (insc.dataInscricao) insc.dataInscricao = paraBR(insc.dataInscricao);
    if (insc.dataAprovacaoSE)
      insc.dataAprovacaoSE = paraBR(insc.dataAprovacaoSE);
    if (insc.dataTransplante)
      insc.dataTransplante = paraBR(insc.dataTransplante);

    setDados({
      ...(paciente.avaliacaoClinica || {}),
      inscricaoTx: insc,
    });
    setEditing(true);
  };

  const handleSalvar = async () => {
    // B318-FU (AI_143, M5): NÃO despejar os dados clínicos no console (LGPD) —
    // handleSalvar dispara a cada salvamento da aba. Só a confirmação da ação.
    console.log("💾 Salvando avaliação clínica...");
    if (!dados) {
      alert("Nenhum dado para salvar");
      return;
    }
    const { inscricaoTx, ...avaliacaoClinica } = dados;

    // Converter DD/MM/AAAA → AAAA-MM-DD para padronizar datas no Firebase
    const paraISO = (data: string): string => {
      if (!data) return data;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
        const p = data.split("/");
        return `${p[2]}-${p[1]}-${p[0]}`;
      }
      return data;
    };

    // Normalizar datas no inscricaoTx antes de salvar
    if (inscricaoTx?.dataInscricao)
      inscricaoTx.dataInscricao = paraISO(inscricaoTx.dataInscricao);
    if (inscricaoTx?.dataAprovacaoSE)
      inscricaoTx.dataAprovacaoSE = paraISO(inscricaoTx.dataAprovacaoSE);
    if (inscricaoTx?.dataTransplante)
      inscricaoTx.dataTransplante = paraISO(inscricaoTx.dataTransplante);

    // 📅 B296 — nenhum marco pode ter data futura (permite hoje/retroativo).
    const marcosData: Array<[string, any]> = [
      ["Data do diagnóstico de CHC", (avaliacaoClinica as any).hccDataDiagnostico],
      ["Data da última ingesta de álcool", (avaliacaoClinica as any).ultimaIngestaAlcool],
      ["Data do TIPS", (avaliacaoClinica as any).tipsData],
      ["Início da diálise", (avaliacaoClinica as any).dialiseDesde],
      ["Data da CTPH", (avaliacaoClinica as any).colangioTransparietoHepaticaData],
      ["Data de inscrição", inscricaoTx?.dataInscricao],
      ["Data de aprovação SE", inscricaoTx?.dataAprovacaoSE],
      ["Data do transplante", inscricaoTx?.dataTransplante],
    ];
    const marcoFuturo = marcosData.find(([, v]) => ehDataFutura(v));
    if (marcoFuturo) {
      alert(msgDataFutura(marcoFuturo[0]));
      return;
    }

    // ✅ Sincronizar campos planos para Dashboard e IA
    const camposPlanos: any = {};
    if (inscricaoTx?.numeroRGCT)
      camposPlanos.numeroRGCT = inscricaoTx.numeroRGCT;
    if (inscricaoTx?.dataInscricao)
      camposPlanos.dataInscricao = inscricaoTx.dataInscricao;
    if (inscricaoTx?.situacaoEspecial !== undefined)
      camposPlanos.situacaoEspecial =
        inscricaoTx.situacaoEspecial === "Sim" ||
        inscricaoTx.situacaoEspecial === true;
    if (inscricaoTx?.dataAprovacaoSE)
      camposPlanos.dataAprovacaoSE = inscricaoTx.dataAprovacaoSE;
    if (inscricaoTx?.tipoSE) camposPlanos.tipoSE = inscricaoTx.tipoSE;
    if (inscricaoTx?.dataTransplante)
      camposPlanos.dataTransplante = inscricaoTx.dataTransplante;
    if (inscricaoTx?.hospitalTransplante)
      camposPlanos.hospitalTransplante = inscricaoTx.hospitalTransplante;

    await atualizarPaciente(pacienteId, {
      avaliacaoClinica,
      inscricaoTx,
      ...camposPlanos,
    });
    setEditing(false);
  };

  const handleCancelar = () => {
    setEditing(false);
    setDados(null);
  };

  // Buscar MELD e Child-Pugh do último exame laboratorial PRÉ-TX.
  // ⚠️ B249/AI_113 — num paciente TRANSPLANTADO pode haver exames pós-Tx SEM a
  // etiqueta tipoExame:"pos" (antigos ou de caminhos que não a marcam). Eles
  // vazam por este filtro (undefined !== "pos") e, sendo mais recentes,
  // escondiam o exame pré-Tx recém-digitado (modoPreForcado) — que É o que tem
  // MELD/Child. Por isso pegamos o exame mais recente que de fato TENHA MELD ou
  // Child (não só o mais recente), caindo no mais recente se nenhum tiver score.
  const examesPaciente = exames
    .filter((e) => e.pacienteId === pacienteId && e.tipoExame !== "pos")
    .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());

  // B248-FU (AI_118): escolhe o exame-base considerando o MELD calculável AO VIVO
  // (não só o `meld` gravado) — senão um exame com os componentes mas sem meld
  // persistido era pulado e a Avaliação mostrava "—" (a planilha mostrava o MELD).
  const sexoFeminino = paciente.sexo === "F";
  const ultimoExame =
    examesPaciente.find(
      (e) => meldVivoDoExame(e, sexoFeminino) > 0 || e.childPugh
    ) ?? examesPaciente[0];
  // Log de diagnóstico LGPD-safe: id/tipo/data do exame escolhido, SEM os
  // escores clínicos (MELD/Child não vão ao console do navegador).
  console.log(
    "🔍 Exame base da Avaliação Clínica:",
    ultimoExame?.id,
    "tipo:",
    ultimoExame?.tipoExame,
    "data:",
    ultimoExame?.data
  );
  const meld = ultimoExame ? meldVivoDoExame(ultimoExame, sexoFeminino) : 0;
  const childPugh = ultimoExame?.childPugh ?? null;
  // Data do exame que originou os escores — exibida junto aos cards p/ tornar
  // visível eventual defasagem (B249/AI_113: o exame escolhido pode não ser o
  // mais recente em absoluto, e sim o mais recente COM score).
  const dataExameScore = ultimoExame?.data ?? null;
  // Máscara para data brasileira DD/MM/AAAA
  const mascaraData = (valor: string): string => {
    const nums = valor.replace(/\D/g, "").slice(0, 8);
    if (nums.length <= 2) return nums;
    if (nums.length <= 4) return `${nums.slice(0, 2)}/${nums.slice(2)}`;
    return `${nums.slice(0, 2)}/${nums.slice(2, 4)}/${nums.slice(4)}`;
  };

  // Converter YYYY-MM-DD para DD/MM/AAAA
  const formatarDataBR = (data: string): string => {
    if (!data) return "";
    if (data.includes("/")) return data;
    const partes = data.split("-");
    if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
    return data;
  };
  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "20px",
        maxHeight: "calc(100vh - 300px)",
        overflowY: "auto",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ ...sectionHeaderStyle, border: "none", margin: 0 }}>
          <ClipboardList size={18} /> Avaliação Clínica
        </h2>
        <div style={{ display: "flex", gap: "8px" }}>
          {somenteLeitura ? null : editing ? (
            <>
              <button
                onClick={handleCancelar}
                style={{
                  background: theme.gray[200],
                  color: theme.gray[700],
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <X size={14} /> Cancelar
              </button>
              <button
                onClick={handleSalvar}
                style={{
                  background: theme.primary[600],
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Save size={14} /> Salvar
              </button>
            </>
          ) : (
            <button
              onClick={handleEditar}
              style={{
                background: theme.primary[600],
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: theme.borderRadius.md,
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Pencil size={14} /> Editar Dados
            </button>
          )}
        </div>
      </div>

      {/* ========== SEÇÃO 1: AVALIAÇÃO CLÍNICA ========== */}
      <div style={{ marginBottom: "32px" }}>
        <h3 style={sectionHeaderStyle}>
          <Activity size={16} /> Avaliação Clínica
        </h3>

        {/* CAUSA DA CIRROSE */}
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>
            <Microscope size={14} /> Causa da cirrose ou doença hepática
          </label>
          <select
            value={av.causaCirrose || ""}
            onChange={(e) => set("causaCirrose", e.target.value)}
            style={selectStyle(editing)}
          >
            <option value="">Selecione...</option>
            {CAUSAS_CIRROSE.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* ABSTINÊNCIA ALCOÓLICA (AI_141) — só p/ etiologia alcoólica. O médico
            informa a última ingesta; o sistema calcula o tempo. Critério vigente
            de listagem = 6 meses (⟳ cairá p/ 3 na nova portaria; ver
            abstinenciaAlcoolica.ts). */}
        {(av.causaCirrose || "").toLowerCase().includes("alco") && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.gray[200]}`,
            }}
          >
            <label style={labelStyle}>
              <AlertTriangle size={14} /> Abstinência alcoólica
            </label>
            <div>
              <label style={labelStyle}>Data da última ingesta</label>
              <input
                type="date"
                value={av.ultimaIngestaAlcool || ""}
                max={hojeISO()}
                onChange={(e) => set("ultimaIngestaAlcool", e.target.value)}
                disabled={!editing}
                style={fieldStyle(editing)}
              />
            </div>
            {(() => {
              const abst = calcularAbstinencia(av.ultimaIngestaAlcool);
              if (!abst) return null;
              const cor = abst.atingeCriterio ? "#15803d" : "#ea580c";
              return (
                <div style={{ marginTop: "10px" }}>
                  <label style={labelStyle}>Tempo de abstinência</label>
                  <div
                    style={{
                      fontWeight: 700,
                      color: cor,
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {abst.texto}
                    {abst.atingeCriterio
                      ? ` — atinge o critério vigente (${MESES_ABSTINENCIA_EXIGIDOS} meses)`
                      : ` — abaixo do critério vigente (${MESES_ABSTINENCIA_EXIGIDOS} meses)`}
                  </div>
                  <p
                    style={{
                      fontSize: "11px",
                      color: theme.gray[500],
                      marginTop: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Critério atual de listagem: {MESES_ABSTINENCIA_EXIGIDOS}{" "}
                    meses (a nova portaria reduzirá para 3).
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* HEPATOCARCINOMA (CHC) — diagnóstico estruturado (B248/AI_111) */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Microscope size={14} /> Hepatocarcinoma (CHC)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.hepatocarcinoma ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("hepatocarcinoma", true)}
              style={simNaoStyle(av.hepatocarcinoma, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("hepatocarcinoma", false)}
              style={simNaoStyle(av.hepatocarcinoma, editing, false)}
            >
              Não
            </button>
          </div>
          {av.hepatocarcinoma && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>Data do diagnóstico</label>
                <input
                  type="date"
                  value={av.hccDataDiagnostico || ""}
                  max={hojeISO()}
                  onChange={(e) => set("hccDataDiagnostico", e.target.value)}
                  disabled={!editing}
                  style={fieldStyle(editing)}
                />
              </div>
              <div>
                <label style={labelStyle}>Critério de Milão</label>
                <select
                  value={av.hccMilao || ""}
                  onChange={(e) => set("hccMilao", e.target.value)}
                  disabled={!editing}
                  style={selectStyle(editing)}
                >
                  <option value="">Selecione...</option>
                  <option value="Dentro">Dentro de Milão</option>
                  <option value="Fora">Fora de Milão</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Estádio BCLC</label>
                <select
                  value={av.hccBCLC || ""}
                  onChange={(e) => set("hccBCLC", e.target.value)}
                  disabled={!editing}
                  style={selectStyle(editing)}
                >
                  <option value="">Selecione...</option>
                  <option value="0">0 (muito precoce)</option>
                  <option value="A">A (precoce)</option>
                  <option value="B">B (intermediário)</option>
                  <option value="C">C (avançado)</option>
                  <option value="D">D (terminal)</option>
                </select>
              </div>
            </div>
          )}
          {av.hepatocarcinoma && (
            <div style={{ marginTop: "12px" }}>
              {/* AI_141 — dois eixos: MODALIDADE (o que foi feito) × INTENÇÃO
                  (para quê). Downstaging/ponte NÃO são modalidades — são a
                  intenção. Terapia sistêmica (sorafenibe/imunoterapia) é
                  modalidade. */}
              <label style={labelStyle}>Terapia prévia do CHC</label>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: av.hccTerapiaLocorregional ? "10px" : "0",
                }}
              >
                <button
                  onClick={() =>
                    editing && set("hccTerapiaLocorregional", true)
                  }
                  style={simNaoStyle(av.hccTerapiaLocorregional, editing, true)}
                >
                  Sim
                </button>
                <button
                  onClick={() =>
                    editing && set("hccTerapiaLocorregional", false)
                  }
                  style={simNaoStyle(av.hccTerapiaLocorregional, editing, false)}
                >
                  Não
                </button>
              </div>
              {av.hccTerapiaLocorregional && (
                <>
                  <label style={labelStyle}>Modalidade(s)</label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "10px",
                    }}
                  >
                    {[
                      "TACE",
                      "Ablação",
                      "Radioembolização",
                      "Terapia sistêmica",
                    ].map((t) => {
                      const marcado = (
                        av.hccTerapiaLocorregionalTipos || []
                      ).includes(t);
                      return (
                        <button
                          key={t}
                          onClick={() => {
                            if (!editing) return;
                            const atual =
                              av.hccTerapiaLocorregionalTipos || [];
                            set(
                              "hccTerapiaLocorregionalTipos",
                              marcado
                                ? atual.filter((x: string) => x !== t)
                                : [...atual, t]
                            );
                          }}
                          style={simNaoStyle(marcado, editing, true)}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  <label style={labelStyle}>Intenção</label>
                  <select
                    value={av.hccTerapiaIntencao || ""}
                    onChange={(e) => set("hccTerapiaIntencao", e.target.value)}
                    disabled={!editing}
                    style={selectStyle(editing)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Ponte">
                      Ponte (bridging) — dentro de Milão, evitar progressão na
                      lista
                    </option>
                    <option value="Downstaging">
                      Downstaging — fora de Milão, reduzir para dentro
                    </option>
                    <option value="Definitivo">
                      Tratamento definitivo (curativo/paliativo)
                    </option>
                  </select>
                </>
              )}
            </div>
          )}
        </div>

        {/* ASCITE */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Ascite
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.ascite ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("ascite", true)}
              style={simNaoStyle(av.ascite, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("ascite", false)}
              style={simNaoStyle(av.ascite, editing, false)}
            >
              Não
            </button>
          </div>
          {av.ascite && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <div>
                <label style={labelStyle}>Paracentese prévia</label>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: av.paracentese ? "10px" : "0",
                  }}
                >
                  <button
                    onClick={() => editing && set("paracentese", true)}
                    style={simNaoStyle(av.paracentese, editing, true)}
                  >
                    Sim
                  </button>
                  <button
                    onClick={() => editing && set("paracentese", false)}
                    style={simNaoStyle(av.paracentese, editing, false)}
                  >
                    Não
                  </button>
                </div>
                {av.paracentese && (
                  <div
                    style={{
                      marginTop: "8px",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label style={labelStyle}>Número de paracenteses</label>
                      <input
                        type="number"
                        min="0"
                        value={av.paracenteseNumero || ""}
                        onChange={(e) =>
                          set("paracenteseNumero", e.target.value)
                        }
                        style={fieldStyle(editing)}
                        placeholder="Ex: 4"
                        readOnly={!editing}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        Maior volume retirado (litros)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={av.paracenteseVolume || ""}
                        onChange={(e) =>
                          set("paracenteseVolume", e.target.value)
                        }
                        style={fieldStyle(editing)}
                        placeholder="Ex: 8.5"
                        readOnly={!editing}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* HIDROTÓRAX HEPÁTICO (AI_141) */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Hidrotórax hepático
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.hidrotorax ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("hidrotorax", true)}
              style={simNaoStyle(av.hidrotorax, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("hidrotorax", false)}
              style={simNaoStyle(av.hidrotorax, editing, false)}
            >
              Não
            </button>
          </div>
          {av.hidrotorax && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>Número de toracocenteses</label>
                <input
                  type="number"
                  min="0"
                  value={av.hidrotoraxSessoes || ""}
                  onChange={(e) => set("hidrotoraxSessoes", e.target.value)}
                  style={fieldStyle(editing)}
                  placeholder="Ex: 2"
                  readOnly={!editing}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Maior volume retirado (litros)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={av.hidrotoraxVolume || ""}
                  onChange={(e) => set("hidrotoraxVolume", e.target.value)}
                  style={fieldStyle(editing)}
                  placeholder="Ex: 1.5"
                  readOnly={!editing}
                />
              </div>
            </div>
          )}
        </div>

        {/* EMPIEMA PLEURAL BACTERIANO ESPONTÂNEO (AI_141) — infecção espontânea
            do hidrotórax hepático (análogo à PBE no espaço pleural). */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Microscope size={14} /> Empiema pleural bacteriano espontâneo
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() =>
                editing && set("empiemaBacterianoEspontaneo", true)
              }
              style={simNaoStyle(
                av.empiemaBacterianoEspontaneo,
                editing,
                true
              )}
            >
              Sim
            </button>
            <button
              onClick={() =>
                editing && set("empiemaBacterianoEspontaneo", false)
              }
              style={simNaoStyle(
                av.empiemaBacterianoEspontaneo,
                editing,
                false
              )}
            >
              Não
            </button>
          </div>
        </div>

        {/* VARIZES */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Varizes
          </label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>Varizes de esôfago</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => editing && set("varizesEsofago", true)}
                  style={simNaoStyle(av.varizesEsofago, editing, true)}
                >
                  Sim
                </button>
                <button
                  onClick={() => editing && set("varizesEsofago", false)}
                  style={simNaoStyle(av.varizesEsofago, editing, false)}
                >
                  Não
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Varizes gástricas</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => editing && set("varizesGastrica", true)}
                  style={simNaoStyle(av.varizesGastrica, editing, true)}
                >
                  Sim
                </button>
                <button
                  onClick={() => editing && set("varizesGastrica", false)}
                  style={simNaoStyle(av.varizesGastrica, editing, false)}
                >
                  Não
                </button>
              </div>
            </div>
          </div>
          <div>
            <label style={labelStyle}>
              HDA prévia (Hemorragia Digestiva Alta)
            </label>
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: av.hdaPrevia ? "10px" : "0",
              }}
            >
              <button
                onClick={() => editing && set("hdaPrevia", true)}
                style={simNaoStyle(av.hdaPrevia, editing, true)}
              >
                Sim
              </button>
              <button
                onClick={() => editing && set("hdaPrevia", false)}
                style={simNaoStyle(av.hdaPrevia, editing, false)}
              >
                Não
              </button>
            </div>
            {av.hdaPrevia && (
              <div style={{ marginTop: "10px", maxWidth: "calc(50% - 6px)" }}>
                <label style={labelStyle}>Número de episódios</label>
                <input
                  type="number"
                  min="0"
                  value={av.hdaEpisodios || ""}
                  onChange={(e) => set("hdaEpisodios", e.target.value)}
                  style={fieldStyle(editing)}
                  placeholder="Ex: 2"
                  readOnly={!editing}
                />
              </div>
            )}
          </div>
        </div>

        {/* LIGADURA ELÁSTICA — desacoplada da HDA (AI_141): aparece quando há
            varizes (profilaxia primária/preemptiva, sem sangramento prévio) OU
            HDA prévia. Antes só surgia dentro de "HDA = Sim". `ligaduraElastica`
            no OR evita esconder um registro já existente em dado legado. */}
        {(av.varizesEsofago ||
          av.varizesGastrica ||
          av.hdaPrevia ||
          av.ligaduraElastica) && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.gray[200]}`,
            }}
          >
            <label style={labelStyle}>
              <Activity size={14} /> Ligadura elástica de varizes
            </label>
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: av.ligaduraElastica ? "12px" : "0",
              }}
            >
              <button
                onClick={() => editing && set("ligaduraElastica", true)}
                style={simNaoStyle(av.ligaduraElastica, editing, true)}
              >
                Sim
              </button>
              <button
                onClick={() => editing && set("ligaduraElastica", false)}
                style={simNaoStyle(av.ligaduraElastica, editing, false)}
              >
                Não
              </button>
            </div>
            {av.ligaduraElastica && (
              <div style={{ maxWidth: "calc(50% - 6px)" }}>
                <label style={labelStyle}>Número de sessões</label>
                <input
                  type="number"
                  min="0"
                  value={av.ligaduraSessoes || ""}
                  onChange={(e) => set("ligaduraSessoes", e.target.value)}
                  style={fieldStyle(editing)}
                  placeholder="Ex: 3"
                  readOnly={!editing}
                />
              </div>
            )}
          </div>
        )}

        {/* TIPS — shunt portossistêmico transjugular (AI_141) */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> TIPS (shunt portossistêmico transjugular)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.tips ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("tips", true)}
              style={simNaoStyle(av.tips, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("tips", false)}
              style={simNaoStyle(av.tips, editing, false)}
            >
              Não
            </button>
          </div>
          {av.tips && (
            <div style={{ maxWidth: "calc(50% - 6px)" }}>
              <label style={labelStyle}>Data do procedimento</label>
              <input
                type="date"
                value={av.tipsData || ""}
                max={hojeISO()}
                onChange={(e) => set("tipsData", e.target.value)}
                disabled={!editing}
                style={fieldStyle(editing)}
              />
            </div>
          )}
        </div>

        {/* ENCEFALOPATIA */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Brain size={14} /> Encefalopatia hepática
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.encefalopatia ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("encefalopatia", true)}
              style={simNaoStyle(av.encefalopatia, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("encefalopatia", false)}
              style={simNaoStyle(av.encefalopatia, editing, false)}
            >
              Não
            </button>
          </div>
          {av.encefalopatia && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>Número de episódios</label>
                <input
                  type="number"
                  min="0"
                  value={av.encefalopatiaEpisodios || ""}
                  onChange={(e) =>
                    set("encefalopatiaEpisodios", e.target.value)
                  }
                  style={fieldStyle(editing)}
                  placeholder="Ex: 3"
                  readOnly={!editing}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Maior grau atingido (West Haven 1-4)
                </label>
                <select
                  value={av.encefalopatiaGrau || ""}
                  onChange={(e) => set("encefalopatiaGrau", e.target.value)}
                  style={selectStyle(editing)}
                >
                  <option value="">Selecione...</option>
                  <option value="1">Grau 1 — Alteração leve de atenção</option>
                  <option value="2">Grau 2 — Letargia, desorientação</option>
                  <option value="3">Grau 3 — Sonolência, confusão grave</option>
                  <option value="4">Grau 4 — Coma</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* PBE */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Microscope size={14} /> Peritonite Bacteriana Espontânea (PBE)
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => editing && set("pbe", true)}
              style={simNaoStyle(av.pbe, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("pbe", false)}
              style={simNaoStyle(av.pbe, editing, false)}
            >
              Não
            </button>
          </div>
        </div>

        {/* COLANGITE BACTERIANA DE REPETIÇÃO (AI_141) — recorrente em CEP/CBS e
            em estenose/anastomose biliar. */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Microscope size={14} /> Colangite bacteriana de repetição
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.colangiteRepeticao ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("colangiteRepeticao", true)}
              style={simNaoStyle(av.colangiteRepeticao, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("colangiteRepeticao", false)}
              style={simNaoStyle(av.colangiteRepeticao, editing, false)}
            >
              Não
            </button>
          </div>
          {av.colangiteRepeticao && (
            <div>
              <label style={labelStyle}>Número de episódios</label>
              <input
                type="number"
                min="0"
                value={av.colangiteEpisodios || ""}
                onChange={(e) => set("colangiteEpisodios", e.target.value)}
                style={fieldStyle(editing)}
                placeholder="Ex: 3"
                readOnly={!editing}
              />
            </div>
          )}
        </div>

        {/* COLANGIOGRAFIA / DRENAGEM TRANS-PARIETO-HEPÁTICA (CTPH) (AI_141) —
            procedimento percutâneo de via biliar. */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Colangiografia/drenagem trans-parieto-hepática
            (CTPH)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.colangioTransparietoHepatica ? "12px" : "0",
            }}
          >
            <button
              onClick={() =>
                editing && set("colangioTransparietoHepatica", true)
              }
              style={simNaoStyle(
                av.colangioTransparietoHepatica,
                editing,
                true
              )}
            >
              Sim
            </button>
            <button
              onClick={() =>
                editing && set("colangioTransparietoHepatica", false)
              }
              style={simNaoStyle(
                av.colangioTransparietoHepatica,
                editing,
                false
              )}
            >
              Não
            </button>
          </div>
          {av.colangioTransparietoHepatica && (
            <div>
              <label style={labelStyle}>Data do procedimento</label>
              <input
                type="date"
                value={av.colangioTransparietoHepaticaData || ""}
                max={hojeISO()}
                onChange={(e) =>
                  set("colangioTransparietoHepaticaData", e.target.value)
                }
                disabled={!editing}
                style={fieldStyle(editing)}
              />
            </div>
          )}
        </div>

        {/* SHR */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Síndrome Hepatorrenal (SHR)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.shr ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("shr", true)}
              style={simNaoStyle(av.shr, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("shr", false)}
              style={simNaoStyle(av.shr, editing, false)}
            >
              Não
            </button>
          </div>
          {av.shr && (
            <div>
              <label style={labelStyle}>Tipo</label>
              <select
                value={av.shrTipo || ""}
                onChange={(e) => set("shrTipo", e.target.value)}
                style={selectStyle(editing)}
              >
                <option value="">Selecione...</option>
                <option value="SHR-AKI">SHR-AKI (tipo 1) — Agudo</option>
                <option value="SHR-NAKI">SHR-NAKI (tipo 2) — Crônico</option>
              </select>
            </div>
          )}
        </div>

        {/* TERAPIA DE SUBSTITUIÇÃO RENAL / DIÁLISE (AI_141) */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Terapia de substituição renal (diálise)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.dialise ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("dialise", true)}
              style={simNaoStyle(av.dialise, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("dialise", false)}
              style={simNaoStyle(av.dialise, editing, false)}
            >
              Não
            </button>
          </div>
          {av.dialise && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>Modalidade</label>
                <select
                  value={av.dialiseModalidade || ""}
                  onChange={(e) => set("dialiseModalidade", e.target.value)}
                  disabled={!editing}
                  style={selectStyle(editing)}
                >
                  <option value="">Selecione...</option>
                  <option value="Hemodiálise">Hemodiálise (HD)</option>
                  <option value="Diálise peritoneal">
                    Diálise peritoneal (DP)
                  </option>
                  <option value="CVVH">Hemofiltração contínua (CVVH)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Desde</label>
                <input
                  type="date"
                  value={av.dialiseDesde || ""}
                  max={hojeISO()}
                  onChange={(e) => set("dialiseDesde", e.target.value)}
                  disabled={!editing}
                  style={fieldStyle(editing)}
                />
              </div>
            </div>
          )}
        </div>

        {/* HIPERTENSÃO PORTOPULMONAR (AI_141) */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Wind size={14} /> Hipertensão portopulmonar
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.hipertensaoPortopulmonar ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("hipertensaoPortopulmonar", true)}
              style={simNaoStyle(av.hipertensaoPortopulmonar, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("hipertensaoPortopulmonar", false)}
              style={simNaoStyle(av.hipertensaoPortopulmonar, editing, false)}
            >
              Não
            </button>
          </div>
          {av.hipertensaoPortopulmonar && (
            <div>
              <label style={labelStyle}>Grau</label>
              <select
                value={av.hipertensaoPortopulmonarGrau || ""}
                onChange={(e) =>
                  set("hipertensaoPortopulmonarGrau", e.target.value)
                }
                style={selectStyle(editing)}
              >
                <option value="">Selecione...</option>
                <option value="leve">Leve</option>
                <option value="moderada">Moderada</option>
                <option value="grave">Grave</option>
              </select>
            </div>
          )}
        </div>

        {/* SÍNDROME HEPATOPULMONAR */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Wind size={14} /> Síndrome Hepatopulmonar
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => editing && set("shdHepatopulmonar", true)}
              style={simNaoStyle(av.shdHepatopulmonar, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("shdHepatopulmonar", false)}
              style={simNaoStyle(av.shdHepatopulmonar, editing, false)}
            >
              Não
            </button>
          </div>
        </div>

        {/* PRURIDO INTRATÁVEL (AI_141) */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Prurido intratável
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => editing && set("pruridoIntratavel", true)}
              style={simNaoStyle(av.pruridoIntratavel, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("pruridoIntratavel", false)}
              style={simNaoStyle(av.pruridoIntratavel, editing, false)}
            >
              Não
            </button>
          </div>
        </div>

        {/* INSUFICIÊNCIA HEPÁTICA AGUDA / FULMINANTE (AI_141) — entidade distinta
            do ACLF (agudização sobre cirrose), logo abaixo. */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Insuficiência hepática aguda (fulminante)
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => editing && set("insuficienciaHepaticaAguda", true)}
              style={simNaoStyle(av.insuficienciaHepaticaAguda, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() =>
                editing && set("insuficienciaHepaticaAguda", false)
              }
              style={simNaoStyle(av.insuficienciaHepaticaAguda, editing, false)}
            >
              Não
            </button>
          </div>
        </div>

        {/* ACLF — AGUDIZAÇÃO SOBRE CIRROSE (AI_141) — grau CLIF 1/2/3 */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> ACLF (agudização sobre cirrose)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.aclf ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("aclf", true)}
              style={simNaoStyle(av.aclf, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("aclf", false)}
              style={simNaoStyle(av.aclf, editing, false)}
            >
              Não
            </button>
          </div>
          {av.aclf && (
            <div>
              <label style={labelStyle}>Grau (CLIF)</label>
              <select
                value={av.aclfGrau || ""}
                onChange={(e) => set("aclfGrau", e.target.value)}
                disabled={!editing}
                style={selectStyle(editing)}
              >
                <option value="">Selecione...</option>
                <option value="1">Grau 1 — 1 falência orgânica</option>
                <option value="2">Grau 2 — 2 falências orgânicas</option>
                <option value="3">Grau 3 — ≥3 falências orgânicas</option>
              </select>
            </div>
          )}
        </div>

        {/* SARCOPENIA / FRAGILIDADE */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <User size={14} /> Sarcopenia / Fragilidade (Liver Frailty Index)
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.sarcopenia ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("sarcopenia", true)}
              style={simNaoStyle(av.sarcopenia, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("sarcopenia", false)}
              style={simNaoStyle(av.sarcopenia, editing, false)}
            >
              Não
            </button>
          </div>
          {av.sarcopenia && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>LFI Score</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={av.lfiScore || ""}
                  onChange={(e) => {
                    const score = parseFloat(e.target.value);
                    set("lfiScore", e.target.value);
                    if (!isNaN(score)) {
                      const classificacao =
                        score < 3.2
                          ? "Robusto"
                          : score <= 4.5
                          ? "Pré-frágil"
                          : "Frágil";
                      set("lfiClassificacao", classificacao);
                    }
                  }}
                  style={fieldStyle(editing)}
                  placeholder="Ex: 3.8"
                  readOnly={!editing}
                />
              </div>
              <div>
                <label style={labelStyle}>Classificação</label>
                <input
                  type="text"
                  value={av.lfiClassificacao || ""}
                  readOnly
                  style={{
                    ...fieldStyle(false),
                    background: theme.gray[50],
                    color:
                      av.lfiClassificacao === "Frágil"
                        ? "#dc2626"
                        : av.lfiClassificacao === "Pré-frágil"
                        ? "#ea580c"
                        : "#15803d",
                    fontWeight: "700",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* GRUPO SANGUÍNEO */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Heart size={14} /> Grupo Sanguíneo
          </label>
          <input
            type="text"
            value={paciente.grupoSanguineo || "Não informado"}
            readOnly
            style={{
              ...fieldStyle(false),
              fontWeight: "700",
              color: theme.primary[700],
              background: theme.gray[50],
            }}
          />
          <p
            style={{
              fontSize: "11px",
              color: theme.gray[500],
              marginTop: "4px",
              fontFamily: theme.fonts.sans,
            }}
          >
            Editar em "Dados do Paciente"
          </p>
        </div>

        {/* TROMBOSE DE VEIA PORTA */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Activity size={14} /> Trombose de Veia Porta
          </label>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: av.trombosePorta ? "12px" : "0",
            }}
          >
            <button
              onClick={() => editing && set("trombosePorta", true)}
              style={simNaoStyle(av.trombosePorta, editing, true)}
            >
              Sim
            </button>
            <button
              onClick={() => editing && set("trombosePorta", false)}
              style={simNaoStyle(av.trombosePorta, editing, false)}
            >
              Não
            </button>
          </div>
          {av.trombosePorta && (
            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>Classificação de Yerdel</label>
              <select
                value={av.yerdel || ""}
                onChange={(e) => set("yerdel", e.target.value)}
                style={selectStyle(editing)}
              >
                <option value="">Selecione...</option>
                <option value="I">
                  Grau I — Trombose parcial (&lt;50%) da veia porta ± extensão
                  mínima para VMS
                </option>
                <option value="II">
                  Grau II — Trombose &gt;50% ou oclusão completa da veia porta ±
                  extensão mínima para VMS
                </option>
                <option value="III">
                  Grau III — Trombose completa da veia porta e VMS proximal, VMS
                  distal pérvia
                </option>
                <option value="IV">
                  Grau IV — Trombose completa da veia porta e VMS (proximal e
                  distal)
                </option>
              </select>
            </div>
          )}
        </div>

        {/* COMORBIDADES */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <Heart size={14} /> Comorbidades
          </label>
          <textarea
            value={av.comorbidades || ""}
            onChange={(e) => set("comorbidades", e.target.value)}
            style={{
              ...fieldStyle(editing),
              minHeight: "80px",
              resize: "vertical",
            }}
            placeholder="Ex: HAS, DM2, IRC, cardiopatia..."
            readOnly={!editing}
          />
        </div>

        {/* MEDICAÇÕES EM USO */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <FileText size={14} /> Medicações em Uso
          </label>
          <textarea
            value={av.medicacoesEmUso || ""}
            onChange={(e) => set("medicacoesEmUso", e.target.value)}
            style={{
              ...fieldStyle(editing),
              minHeight: "80px",
              resize: "vertical",
            }}
            placeholder="Ex: Propranolol 40mg 12/12h, Furosemida 40mg/dia, Lactulose 30ml 8/8h..."
            readOnly={!editing}
          />
        </div>

        {/* CIRURGIAS PRÉVIAS */}
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.gray[200]}`,
          }}
        >
          <label style={labelStyle}>
            <ClipboardList size={14} /> Cirurgias Prévias
          </label>
          <textarea
            value={av.cirurgiasPrevias || ""}
            onChange={(e) => set("cirurgiasPrevias", e.target.value)}
            style={{
              ...fieldStyle(editing),
              minHeight: "80px",
              resize: "vertical",
            }}
            placeholder="Ex: Colecistectomia (2019), Esplenectomia (2020)..."
            readOnly={!editing}
          />
        </div>
      </div>
      {/* ========== SEÇÃO 2: ESCORES ========== */}
      <div style={{ marginBottom: "32px" }}>
        <h3 style={sectionHeaderStyle}>
          <Activity size={16} /> Escores de Gravidade
        </h3>
        <p
          style={{
            fontSize: "12px",
            color: theme.gray[500],
            marginBottom: "12px",
            fontFamily: theme.fonts.sans,
          }}
        >
          Calculados automaticamente a partir dos exames laboratoriais.
          {dataExameScore && (meld > 0 || childPugh) && (
            <span style={{ color: theme.gray[600], fontWeight: 600 }}>
              {" "}
              (exame de {dataExameScore})
            </span>
          )}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <div
            style={{
              padding: "16px",
              background:
                meld >= 25 ? "#fef2f2" : meld >= 19 ? "#fff7ed" : "#f0f9ff",
              border: `1px solid ${
                meld >= 25 ? "#fca5a5" : meld >= 19 ? "#fed7aa" : "#bae6fd"
              }`,
              borderRadius: theme.borderRadius.md,
              textAlign: "center",
            }}
          >
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "12px",
                color: theme.gray[600],
                fontFamily: theme.fonts.sans,
              }}
            >
              MELD 3.0
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "28px",
                fontWeight: "800",
                color:
                  meld >= 25
                    ? "#dc2626"
                    : meld >= 19
                    ? "#ea580c"
                    : theme.primary[600],
                fontFamily: theme.fonts.sans,
              }}
            >
              {meld > 0 ? meld : "—"}
            </p>
          </div>
          <div
            style={{
              padding: "16px",
              background: childPugh?.startsWith("C")
                ? "#fef2f2"
                : childPugh?.startsWith("B")
                ? "#fffbeb"
                : "#f0fdf4",
              border: `1px solid ${
                childPugh?.startsWith("C")
                  ? "#fca5a5"
                  : childPugh?.startsWith("B")
                  ? "#fde68a"
                  : "#bbf7d0"
              }`,
              borderRadius: theme.borderRadius.md,
              textAlign: "center",
            }}
          >
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "12px",
                color: theme.gray[600],
                fontFamily: theme.fonts.sans,
              }}
            >
              Child-Pugh
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "28px",
                fontWeight: "800",
                color: childPugh?.startsWith("C")
                  ? "#dc2626"
                  : childPugh?.startsWith("B")
                  ? "#d97706"
                  : "#15803d",
                fontFamily: theme.fonts.sans,
              }}
            >
              {childPugh ?? "—"}
            </p>
          </div>
        </div>
      </div>

      {/* ========== SEÇÃO 3: INSCRIÇÃO PARA TRANSPLANTE ========== */}
      <div>
        <h3 style={sectionHeaderStyle}>
          <Hospital size={16} /> Inscrição para Transplante
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* NÚMERO RGCT */}
          <div>
            <label style={labelStyle}>
              <FileText size={14} /> Número RGCT
            </label>
            <input
              type="text"
              value={
                editing
                  ? dados?.inscricaoTx?.numeroRGCT || ""
                  : insc?.numeroRGCT || ""
              }
              onChange={(e) => setInsc("numeroRGCT", e.target.value)}
              style={fieldStyle(editing)}
              placeholder="Número do RGCT"
              readOnly={!editing}
            />
          </div>

          {/* DATA DE INSCRIÇÃO */}
          <div>
            <label style={labelStyle}>
              <FileText size={14} /> Data de Inscrição
            </label>
            <input
              type="text"
              value={
                editing
                  ? dados?.inscricaoTx?.dataInscricao || ""
                  : formatarDataBR(insc?.dataInscricao || "")
              }
              onChange={(e) =>
                setInsc("dataInscricao", mascaraData(e.target.value))
              }
              style={fieldStyle(editing)}
              placeholder="DD/MM/AAAA"
              readOnly={!editing}
              maxLength={10}
            />
          </div>

          {/* MELD-Na DA INSCRIÇÃO (B218, AI_101)
              ⚠️ CAMPO TRANSITÓRIO: o MELD-Na é o score vigente da Central de SP
              até a Portaria 8.041/2025 entrar em vigor; quando isso ocorrer o
              MELD-Na sai (MELD 3.0 vira oficial) e este campo deve ser aposentado.
              Alimenta inscricaoTx.meldNa → scores.meldNa → critério "MELD>10" e
              banda de validade do laudo SE-CHC (torna determinístico; antes só
              existia em texto livre). */}
          <div>
            <label style={labelStyle}>
              <FileText size={14} /> MELD-Na da inscrição
            </label>
            <input
              type="number"
              min={6}
              max={40}
              value={
                editing
                  ? dados?.inscricaoTx?.meldNa ?? ""
                  : insc?.meldNa ?? ""
              }
              onChange={(e) => setInsc("meldNa", e.target.value)}
              style={fieldStyle(editing)}
              placeholder="Score vigente na Central (ex.: 15)"
              readOnly={!editing}
            />
          </div>

          {/* SITUAÇÃO ESPECIAL */}
          <div>
            <label style={labelStyle}>
              <AlertTriangle size={14} /> Situação Especial
            </label>
            <select
              value={
                editing
                  ? dados?.inscricaoTx?.situacaoEspecial || ""
                  : insc?.situacaoEspecial || ""
              }
              onChange={(e) => setInsc("situacaoEspecial", e.target.value)}
              style={selectStyle(editing)}
            >
              <option value="">Selecione...</option>
              <option value="Sim">Sim</option>
              <option value="Não">Não</option>
            </select>
          </div>

          {/* DATA APROVAÇÃO SE — só se SE = Sim */}
          {(editing
            ? dados?.inscricaoTx?.situacaoEspecial
            : insc?.situacaoEspecial) === "Sim" && (
            <div>
              <label style={labelStyle}>
                <FileText size={14} /> Data de Aprovação SE
              </label>
              <input
                type="text"
                value={
                  editing
                    ? dados?.inscricaoTx?.dataAprovacaoSE || ""
                    : formatarDataBR(insc?.dataAprovacaoSE || "")
                }
                onChange={(e) =>
                  setInsc("dataAprovacaoSE", mascaraData(e.target.value))
                }
                style={fieldStyle(editing)}
                placeholder="DD/MM/AAAA"
                readOnly={!editing}
                maxLength={10}
              />
            </div>
          )}

          {/* TIPO SE — só se SE = Sim */}
          {(editing
            ? dados?.inscricaoTx?.situacaoEspecial
            : insc?.situacaoEspecial) === "Sim" && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>
                <AlertTriangle size={14} /> Tipo de Situação Especial
              </label>
              <select
                value={
                  editing
                    ? dados?.inscricaoTx?.tipoSE || ""
                    : insc?.tipoSE || ""
                }
                onChange={(e) => setInsc("tipoSE", e.target.value)}
                style={selectStyle(editing)}
              >
                <option value="">Selecione...</option>
                {TIPOS_SE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* DATA DO TRANSPLANTE */}
          <div>
            <label style={labelStyle}>
              <Hospital size={14} /> Data do Transplante
            </label>
            <input
              type="text"
              value={
                editing
                  ? dados?.inscricaoTx?.dataTransplante || ""
                  : formatarDataBR(insc?.dataTransplante || "")
              }
              onChange={(e) =>
                setInsc("dataTransplante", mascaraData(e.target.value))
              }
              style={fieldStyle(editing)}
              placeholder="DD/MM/AAAA"
              readOnly={!editing}
              maxLength={10}
            />
            <p
              style={{
                fontSize: "11px",
                color: theme.gray[500],
                marginTop: "4px",
                fontFamily: theme.fonts.sans,
              }}
            >
              ⚠️ Ao preencher, o prontuário mudará para pós-transplante
            </p>
          </div>

          {/* CENTRO TRANSPLANTADOR */}
          <div>
            <label style={labelStyle}>
              <Hospital size={14} /> Centro Transplantador
            </label>
            <input
              type="text"
              value={
                editing
                  ? dados?.inscricaoTx?.hospitalTransplante || ""
                  : insc?.hospitalTransplante || ""
              }
              onChange={(e) => setInsc("hospitalTransplante", e.target.value)}
              style={fieldStyle(editing)}
              placeholder="Nome do hospital"
              readOnly={!editing}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
